/**
 * 処理工程（シート）— 12. 工程のタイマー
 *
 * フィルム／印画紙 RC／印画紙 FB で工程を分ける（以前は 1 つの一覧に印画紙の工程が混ざっていた）。
 * - 各工程の「開始」で時間を計り、終わると完了音を鳴らしてチェックを付ける
 * - 次の工程は液の入れ替えがあるので、自動では始めない（手で開始する）
 * - 工程ごとの時間は ± 15 秒で調整でき、端末に覚える（zn.process.times）
 * - シートを閉じても計り続ける。現像画面の「処理工程」ボタンに残り時間を出す
 * - フィルムのチェックは従来のキー（zn.dev.checklist）をそのまま使い、旧データを引き継ぐ
 */

import { openSheet, escapeHtml, toast, confirmDialog, buzz, icon } from './ui.js';
import { getCurrentShoot } from './scene.js';
import { formatSeconds } from './data/dev-times.js';
import { unlockAudio, completeBeep } from './signal.js';

const MODES = {
  film: {
    label: 'フィルム',
    checkKey: 'zn.dev.checklist', // 旧版と同じキー
    steps: [
      { id: 'develop', label: '現像', sec: 0, hint: '現像タイマーで計測' },
      { id: 'stop', label: '停止', sec: 30, hint: '酢酸 1.5%・攪拌' },
      { id: 'fix1', label: '定着 1 浴目', sec: 240, hint: '30 秒ごとに攪拌' },
      { id: 'fix2', label: '定着 2 浴目', sec: 240, hint: '新しい定着液で' },
      { id: 'wash1', label: '中間水洗', sec: 300, hint: '流水' },
      { id: 'hypo', label: 'ハイポクリア', sec: 180, hint: 'Permawash など' },
      { id: 'wash2', label: '本水洗', sec: 600, hint: '流水・水温に注意' },
      { id: 'dry', label: '乾燥', sec: 0, hint: '水切り剤のあと吊るして乾かす（30 分〜1 晩）' },
    ],
  },
  rc: {
    label: '印画紙 RC',
    checkKey: 'zn.dev.checklist.rc',
    steps: [
      { id: 'pdev', label: '現像', sec: 60, hint: '連続攪拌' },
      { id: 'pstop', label: '停止', sec: 15, hint: '' },
      { id: 'pfix', label: '定着', sec: 60, hint: '' },
      { id: 'pwash', label: '水洗', sec: 120, hint: '流水' },
      { id: 'pdry', label: '乾燥', sec: 0, hint: 'スクイージ後に網棚' },
    ],
  },
  fb: {
    label: '印画紙 FB',
    checkKey: 'zn.dev.checklist.fb',
    steps: [
      { id: 'fdev', label: '現像', sec: 120, hint: '連続攪拌' },
      { id: 'fstop', label: '停止', sec: 30, hint: '' },
      { id: 'ffix1', label: '定着 1 浴目', sec: 90, hint: '' },
      { id: 'ffix2', label: '定着 2 浴目', sec: 90, hint: '新しい定着液で' },
      { id: 'fwash1', label: '中間水洗', sec: 300, hint: '流水' },
      { id: 'fhypo', label: 'ハイポクリア', sec: 600, hint: '' },
      { id: 'fwash2', label: '本水洗', sec: 3600, hint: '流水' },
      { id: 'fdry', label: '乾燥', sec: 0, hint: 'スクイージ後に網棚' },
    ],
  },
};
const MODE_KEY = 'zn.process.mode';
const TIMES_KEY = 'zn.process.times';

const load = (k, d) => { try { return JSON.parse(localStorage.getItem(k) || 'null') ?? d; } catch { return d; } };
const save = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} };

// 計っている工程（シートを閉じても続く）
const run = { mode: null, id: null, endAt: 0, pausedLeft: 0, running: false, iv: null, wake: null };
const listeners = new Set();
/** 現像画面のボタンなどに状態を知らせる */
export function onProcessChange(fn) { listeners.add(fn); return () => listeners.delete(fn); }
function notify() { listeners.forEach((fn) => { try { fn(); } catch {} }); }

export function checklistProgress(mode = 'film') {
  const checked = load(MODES[mode].checkKey, {});
  return { done: MODES[mode].steps.filter((s) => checked[s.id]).length, total: MODES[mode].steps.length };
}
/** 「定着 1 浴目 2:31」のような現在の工程の表示（計っていなければ null） */
export function runningLabel() {
  if (!run.id) return null;
  const st = MODES[run.mode].steps.find((s) => s.id === run.id);
  return `${st?.label || ''} ${formatSeconds(Math.ceil(leftSec()))}`;
}

function stepSec(mode, step) {
  const t = load(TIMES_KEY, {});
  const v = t[`${mode}.${step.id}`];
  return Number.isFinite(v) ? v : step.sec;
}
function leftSec() {
  return run.running ? Math.max(0, (run.endAt - Date.now()) / 1000) : run.pausedLeft;
}

async function wakeOn() {
  try { if ('wakeLock' in navigator && !run.wake) run.wake = await navigator.wakeLock.request('screen'); } catch {}
}
function wakeOff() {
  try { run.wake?.release(); } catch {}
  run.wake = null;
}

function startStep(mode, step) {
  if (run.id && run.running && !(run.mode === mode && run.id === step.id)) {
    toast(`「${MODES[run.mode].steps.find((s) => s.id === run.id)?.label}」を計っています`);
    return;
  }
  unlockAudio();
  const sec = run.mode === mode && run.id === step.id && run.pausedLeft > 0 ? run.pausedLeft : stepSec(mode, step);
  Object.assign(run, { mode, id: step.id, endAt: Date.now() + sec * 1000, running: true, pausedLeft: 0 });
  wakeOn();
  clearInterval(run.iv);
  run.iv = setInterval(tickRun, 250);
  buzz(10);
  notify();
}
function pauseStep() {
  if (!run.running) return;
  run.pausedLeft = leftSec();
  run.running = false;
  clearInterval(run.iv);
  wakeOff();
  notify();
}
function stopStep() {
  clearInterval(run.iv);
  wakeOff();
  Object.assign(run, { mode: null, id: null, endAt: 0, pausedLeft: 0, running: false });
  notify();
}
function tickRun() {
  if (run.running && leftSec() <= 0) {
    const { mode, id } = run;
    const checked = load(MODES[mode].checkKey, {});
    checked[id] = true;
    save(MODES[mode].checkKey, checked);
    stopStep();
    completeBeep();
    buzz([200, 100, 200]);
    const st = MODES[mode].steps;
    const i = st.findIndex((s) => s.id === id);
    const next = st.slice(i + 1).find((s) => !load(MODES[mode].checkKey, {})[s.id]);
    toast(`${st[i].label} が終わりました${next ? `。次は「${next.label}」（準備ができたら開始）` : ''}`, 4200);
    return;
  }
  notify();
}

export function openProcessChecklist(onChange) {
  let mode = MODES[load(MODE_KEY, 'film')] ? load(MODE_KEY, 'film') : 'film';
  const body = document.createElement('div');
  const headExtra = '<button class="bar__text" type="button" data-ck-reset style="font-size:13px">リセット</button>';
  const sheet = openSheet({ en: 'process', title: '処理工程', body, headExtra, full: true, onClose: () => unsub() });
  const unsub = onProcessChange(() => { paintRunning(); onChange?.(); });
  const devTime = getCurrentShoot()?.development?.targetDevTime;

  function paint() {
    const checked = load(MODES[mode].checkKey, {});
    const prog = checklistProgress(mode);
    const eb = sheet.el.querySelector('.sheet__head .eyebrow');
    if (eb) eb.textContent = `process · ${prog.done}/${prog.total}`;
    body.innerHTML = `
      <div style="padding:0 16px 10px"><div class="seg">${Object.entries(MODES).map(([k, m]) => `<button type="button" class="seg__opt ${k === mode ? 'is-on' : ''}" data-mode="${k}">${m.label}</button>`).join('')}</div></div>
      ${MODES[mode].steps.map((s, i) => {
        const sec = stepSec(mode, s);
        const timed = sec > 0 && !(mode === 'film' && s.id === 'develop');
        const timeLabel = mode === 'film' && s.id === 'develop' ? (devTime ? formatSeconds(devTime) : '計画時間') : sec > 0 ? formatSeconds(sec) : '—';
        return `
        <div class="chk ${checked[s.id] ? 'is-on' : ''}" data-step="${s.id}" style="align-items:center;flex-wrap:wrap">
          <button type="button" class="chk__box" data-check="${s.id}" aria-label="${escapeHtml(s.label)}を完了にする">${checked[s.id] ? '<svg class="i draw" width="18" height="18"><use href="#i-check"/></svg>' : ''}</button>
          <div class="chk__body">
            <div style="font:500 15px/1.3 var(--font-jp-sans)"><span class="en sub" style="font-size:11px;margin-right:6px">${String(i + 1).padStart(2, '0')}</span>${escapeHtml(s.label)}</div>
            ${s.hint ? `<div class="sub" style="font-size:12px;line-height:1.5;margin-top:2px">${escapeHtml(s.hint)}</div>` : ''}
          </div>
          ${timed ? `
            <div style="display:flex;align-items:center;gap:4px;flex:none">
              <button type="button" class="cond__pm" data-adj="-15" data-for="${s.id}" style="width:32px;height:32px" aria-label="15 秒短く">−</button>
              <span class="num" style="min-width:48px;text-align:center;font-size:15px">${formatSeconds(sec)}</span>
              <button type="button" class="cond__pm" data-adj="15" data-for="${s.id}" style="width:32px;height:32px" aria-label="15 秒長く">+</button>
              <button type="button" class="pill pill--sm" data-start="${s.id}" style="margin-left:4px">開始</button>
            </div>` : `<span class="en" style="font:500 13px/1.3 var(--font-en);white-space:nowrap">${escapeHtml(timeLabel)}</span>`}
          <div class="run-line" data-run="${s.id}" style="flex-basis:100%;padding-left:46px"></div>
        </div>`;
      }).join('')}
      <p class="sub" style="font-size:11.5px;line-height:1.6;margin:12px 24px 24px">工程が終わると音が鳴り、チェックが付きます。次の工程は液を替えてから「開始」を押してください。時間は ± で調整でき、次回も使います。</p>`;
    body.querySelectorAll('[data-mode]').forEach((b) => b.addEventListener('click', () => { mode = b.dataset.mode; save(MODE_KEY, mode); buzz(6); paint(); }));
    body.querySelectorAll('[data-check]').forEach((b) => b.addEventListener('click', () => {
      const c = load(MODES[mode].checkKey, {});
      c[b.dataset.check] = !c[b.dataset.check];
      save(MODES[mode].checkKey, c);
      buzz(8);
      paint();
      onChange?.();
    }));
    body.querySelectorAll('[data-adj]').forEach((b) => b.addEventListener('click', () => {
      const st = MODES[mode].steps.find((x) => x.id === b.dataset.for);
      const t = load(TIMES_KEY, {});
      t[`${mode}.${st.id}`] = Math.max(15, Math.min(7200, stepSec(mode, st) + Number(b.dataset.adj)));
      save(TIMES_KEY, t);
      paint();
    }));
    body.querySelectorAll('[data-start]').forEach((b) => b.addEventListener('click', () => {
      const st = MODES[mode].steps.find((x) => x.id === b.dataset.start);
      if (run.id === st.id && run.mode === mode && run.running) pauseStep();
      else startStep(mode, st);
    }));
    paintRunning();
  }

  function paintRunning() {
    body.querySelectorAll('[data-run]').forEach((el) => {
      const active = run.mode === mode && run.id === el.dataset.run;
      el.innerHTML = active
        ? `<div style="display:flex;align-items:center;gap:12px;margin-top:2px"><span class="num" style="font:300 30px/1 var(--font-en);${leftSec() <= 10 ? 'color:var(--color-alert);font-weight:500' : ''}">${formatSeconds(Math.ceil(leftSec()))}</span><span class="sub" style="font-size:12px">${run.running ? '計測中' : '一時停止中'}</span><button type="button" class="sub" data-stop style="margin-left:auto;font:500 12px/1 var(--font-jp-sans);text-decoration:underline;text-underline-offset:3px;padding:6px 0">やめる</button></div>`
        : '';
      el.querySelector('[data-stop]')?.addEventListener('click', () => stopStep());
    });
    body.querySelectorAll('[data-start]').forEach((b) => {
      const active = run.mode === mode && run.id === b.dataset.start;
      b.textContent = active ? (run.running ? '一時停止' : '再開') : '開始';
      b.classList.toggle('is-on', active && run.running);
    });
  }

  paint();
  sheet.el.querySelector('[data-ck-reset]').addEventListener('click', async () => {
    const ok = await confirmDialog({ title: 'チェックをすべて外しますか？', body: `「${MODES[mode].label}」の工程の完了マークをリセットします。`, ok: 'リセット' });
    if (!ok) return;
    save(MODES[mode].checkKey, {});
    paint();
    onChange?.();
    toast('チェックをリセットしました');
  });
  void icon;
}
